import { VideoData } from '@lumen5/luminary';

import { colord } from 'colord';

type AnyRecord = Record<string, unknown>;

export type ChartColorGroup = {
    /**
     * Stable React key — the joined set of config paths this swatch owns. Identity comes from the
     * locations, not the color value, so editing a swatch (or two swatches landing on the same
     * color) never remounts the picker or merges the swatches.
     */
    key: string;
    /** Every config path this swatch controls; editing writes the chosen color to all of them. */
    paths: string[][];
};

function parseThemeColorIndex(value: unknown): number | null {
    if (typeof value !== 'string') return null;
    if (!VideoData.isTemplateReference(value)) return null;
    if (!value.includes('_colors_')) return null;
    const idx = VideoData.getTemplateIndex(value);
    return Number.isInteger(idx) && idx >= 0 ? idx : null;
}

/**
 * True for a value that represents an editable chart color: a template color reference
 * (`themeRef_colors_N`) or a literal `#hex` / `rgb(a)` / `hsl(a)` color. Named CSS colors and
 * the `transparent` keyword are excluded so we never mistake another echarts string (a category
 * name, a position keyword, a label formatter) for a color.
 */
function isColorValue(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    if (parseThemeColorIndex(value) !== null) return true;
    if (!/^#|^rgb|^hsl/i.test(value)) return false;
    return colord(value).isValid();
}

type ColorGroup = 'visualization' | 'text';

function classifyColorGroup(path: string[]): ColorGroup {
    for (let i = path.length - 1; i >= 0; i--) {
        const key = path[i];
        if (key === 'itemStyle' || key === 'lineStyle' || key === 'areaStyle') {
            return 'visualization';
        }
        if (
            key === 'label' ||
            key === 'endLabel' ||
            key === 'textStyle' ||
            key === 'subtextStyle' ||
            key === 'axisLabel' ||
            key === 'axisName'
        ) {
            return 'text';
        }
        // graphic[*] is overwhelmingly text in our chart configs (captions, highlight
        // numbers). If we ever add non-text graphics with themeRef fills, this rule will
        // need to inspect graphic.children[*].type instead.
        if (key === 'graphic') {
            return 'text';
        }
    }
    return 'visualization';
}

function walkColorStringsWithPath(
    node: unknown,
    visit: (value: string, path: string[]) => void,
    path: string[] = [],
): void {
    if (typeof node === 'string') {
        visit(node, path);
        return;
    }
    if (!node || typeof node !== 'object') return;
    if ('$animate' in (node as AnyRecord)) return;
    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
            path.push(String(i));
            walkColorStringsWithPath(node[i], visit, path);
            path.pop();
        }
        return;
    }
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        walkColorStringsWithPath((node as AnyRecord)[key], visit, path);
        path.pop();
    }
}

const PALETTE_DATA_COLOR_TYPES = new Set(['pie', 'funnel']);

function hasExplicitColor(point: unknown): boolean {
    if (!point || typeof point !== 'object') return false;
    const itemStyle = (point as AnyRecord).itemStyle as AnyRecord | undefined;
    return isColorValue(itemStyle?.color);
}

function seriesHasExplicitColor(series: AnyRecord): boolean {
    if (isColorValue(series.color)) return true;
    const itemStyle = series.itemStyle as AnyRecord | undefined;
    const lineStyle = series.lineStyle as AnyRecord | undefined;
    return isColorValue(itemStyle?.color) || isColorValue(lineStyle?.color);
}

/**
 * The set of top-level `color` palette indices the chart actually renders. ECharts auto-assigns
 * palette entries to elements with no explicit color — by data index for pie/funnel
 * (`colorBy: 'data'`), by series index otherwise. Entries past what the data consumes paint
 * nothing, so we drop them rather than show dead swatches.
 */
function getConsumedPaletteIndices(echartsConfig: AnyRecord): Set<number> {
    const consumed = new Set<number>();
    const raw = echartsConfig.series;
    const seriesList = Array.isArray(raw) ? raw : raw ? [raw] : [];
    seriesList.forEach((entry, seriesIndex) => {
        if (!entry || typeof entry !== 'object') return;
        const series = entry as AnyRecord;
        const colorBy =
            (series.colorBy as string) ??
            (PALETTE_DATA_COLOR_TYPES.has(series.type as string) ? 'data' : 'series');
        if (colorBy === 'data') {
            const data = Array.isArray(series.data) ? series.data : [];
            data.forEach((point, i) => {
                if (!hasExplicitColor(point)) consumed.add(i);
            });
        } else if (!seriesHasExplicitColor(series)) {
            consumed.add(seriesIndex);
        }
    });
    return consumed;
}

/** Read the color string at a config path (array indices are encoded as strings). */
export function getColorAtPath(echartsConfig: AnyRecord, path: string[]): string | undefined {
    let node: unknown = echartsConfig;
    for (const key of path) {
        if (!node || typeof node !== 'object') return undefined;
        node = Array.isArray(node) ? node[Number(key)] : (node as AnyRecord)[key];
    }
    return typeof node === 'string' ? node : undefined;
}

function collectImpactfulColorEntries(
    echartsConfig: AnyRecord,
): { value: string; path: string[] }[] {
    const consumed = getConsumedPaletteIndices(echartsConfig);
    const entries: { value: string; path: string[] }[] = [];
    walkColorStringsWithPath(echartsConfig, (value, path) => {
        if (!isColorValue(value)) return;
        // Unused palette slots render nothing — leave them out so every swatch has an effect.
        if (path.length === 2 && path[0] === 'color' && !consumed.has(Number(path[1]))) return;
        entries.push({ value, path: [...path] });
    });
    return entries;
}

/**
 * A value-independent fingerprint of the chart's editable color *locations*. It changes only when
 * the set of color-bearing paths changes (chart-type switch, regenerate, data added/removed) — not
 * when a color is edited or shuffled — so callers can keep a swatch grouping stable across edits.
 */
export function getChartColorStructureSignature(echartsConfig: AnyRecord): string {
    return collectImpactfulColorEntries(echartsConfig)
        .map((entry) => entry.path.join('.'))
        .sort()
        .join('|');
}

/**
 * Group the chart's impactful color locations into swatches. Locations that currently share a
 * color start grouped (so e.g. a bar and a label that match move together), but a swatch's
 * identity is its set of paths, not its color — so it never disappears or merges when colors
 * coincide. Editing a swatch writes the new color to all of its paths.
 */
export function getChartColorGroups(echartsConfig: AnyRecord): ChartColorGroup[] {
    const byValue = new Map<string, string[][]>();
    const order: string[] = [];
    for (const { value, path } of collectImpactfulColorEntries(echartsConfig)) {
        if (!byValue.has(value)) {
            byValue.set(value, []);
            order.push(value);
        }
        byValue.get(value)!.push(path);
    }
    return order.map((value) => {
        const paths = byValue.get(value)!;
        return { key: paths.map((p) => p.join('.')).join('|'), paths };
    });
}

function setColorAtPath(node: unknown, path: string[], value: string): unknown {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    if (Array.isArray(node)) {
        const copy = node.slice();
        copy[Number(head)] = setColorAtPath(node[Number(head)], rest, value);
        return copy;
    }
    const obj = (node && typeof node === 'object' ? node : {}) as AnyRecord;
    return { ...obj, [head]: setColorAtPath(obj[head], rest, value) };
}

export function setChartColorsAtPaths(
    echartsConfig: AnyRecord,
    paths: string[][],
    newColor: string,
): AnyRecord {
    return paths.reduce<unknown>(
        (config, path) => setColorAtPath(config, path, newColor),
        echartsConfig,
    ) as AnyRecord;
}

function fisherYatesInPlace<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
}

/**
 * Pick a permutation of the `used` palette slot values that prefers bringing in fresh, unused
 * template colors first, falling back to permuting among the values already in use (which
 * includes user hex overrides) when no fresh template slots remain. Returns a Map from old
 * value → new value. When the only viable result is an identity mapping (e.g. a single-color
 * group with no other slots available), returns an empty map so callers can no-op.
 */
function buildGroupShuffleMapping(used: string[], templateColorCount: number): Map<string, string> {
    if (used.length === 0) return new Map();

    const usedSet = new Set(used);
    const unusedTemplateRefs: string[] = [];
    for (let i = 0; i < templateColorCount; i++) {
        const ref = VideoData.templateColorByIndex(i);
        if (!usedSet.has(ref)) unusedTemplateRefs.push(ref);
    }

    for (let attempt = 0; attempt < 8; attempt++) {
        const freshColors = [...unusedTemplateRefs];
        const recycledColors = [...used];
        fisherYatesInPlace(freshColors);
        fisherYatesInPlace(recycledColors);
        const pool = [...freshColors, ...recycledColors].slice(0, used.length);

        const candidate = new Map<string, string>();
        let anyChanged = false;
        for (let i = 0; i < used.length; i++) {
            const from = used[i];
            const to = pool[i];
            candidate.set(from, to);
            if (from !== to) anyChanged = true;
        }
        if (anyChanged) return candidate;
    }
    return new Map();
}

function remapColorsByGroup(
    node: unknown,
    mappingByGroup: Record<ColorGroup, Map<string, string>>,
    path: string[],
): unknown {
    if (typeof node === 'string') {
        const next = mappingByGroup[classifyColorGroup(path)].get(node);
        return next ?? node;
    }
    if (!node || typeof node !== 'object') return node;
    if ('$animate' in (node as AnyRecord)) return node;
    if (Array.isArray(node)) {
        return node.map((child, i) => {
            path.push(String(i));
            const result = remapColorsByGroup(child, mappingByGroup, path);
            path.pop();
            return result;
        });
    }
    const result: AnyRecord = {};
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        result[key] = remapColorsByGroup((node as AnyRecord)[key], mappingByGroup, path);
        path.pop();
    }
    return result;
}

export function shuffleChartPalette(
    echartsConfig: AnyRecord,
    templateColorCount: number,
): AnyRecord {
    if (templateColorCount <= 0) return echartsConfig;

    const usedByGroup: Record<ColorGroup, Set<string>> = {
        visualization: new Set(),
        text: new Set(),
    };

    walkColorStringsWithPath(echartsConfig, (value, path) => {
        if (!isColorValue(value)) return;
        usedByGroup[classifyColorGroup(path)].add(value);
    });

    if (usedByGroup.visualization.size === 0 && usedByGroup.text.size === 0) {
        return echartsConfig;
    }

    const mappingByGroup: Record<ColorGroup, Map<string, string>> = {
        visualization: buildGroupShuffleMapping(
            Array.from(usedByGroup.visualization).sort(),
            templateColorCount,
        ),
        text: buildGroupShuffleMapping(Array.from(usedByGroup.text).sort(), templateColorCount),
    };

    const anyChanged =
        Array.from(mappingByGroup.visualization.entries()).some(([k, v]) => k !== v) ||
        Array.from(mappingByGroup.text.entries()).some(([k, v]) => k !== v);
    if (!anyChanged) return echartsConfig;

    return remapColorsByGroup(echartsConfig, mappingByGroup, []) as AnyRecord;
}
